import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Confetti from 'react-confetti';
import { QRCodeCanvas } from 'qrcode.react';
import SignalRService from '../../../utilities/WS/SignalRService';
import {
    GetPublicConfigBL,
    ParticipateInitBL,
    TrackMetricBL,
    SpinRouletteBL,
    generateVoucherCanvas,
    downloadCanvasAsPng,
    attachScreenshotListeners,
    TrackActionBL,
    validateRegistrationForm,
    getDictionary,
    detectInitialLanguage,
    saveParticipantSession,
    getParticipantSession,
    clearParticipantSession,
    getOrCreateSessionId,
    formatEventDate,
    calculateSliceAngle,
    describeArc,
    calculatePegsPositions,
    calculateTargetRotation,
    getDefaultPrizes,
    preloadAssets,
    rouletteAudioEngine,
    triggerHapticTick,
    triggerHapticVictory,
    DEFAULT_TERMS_TEXT_ES,
    DEFAULT_TERMS_TEXT_EN,
    DEFAULT_PRIVACY_TEXT_ES,
    DEFAULT_PRIVACY_TEXT_EN,
    DEFAULT_DO_NOT_SELL_TEXT_ES,
    DEFAULT_DO_NOT_SELL_TEXT_EN,
    DEFAULT_TERMS_TEXT_MX_ES,
    DEFAULT_TERMS_TEXT_MX_EN,
    DEFAULT_PRIVACY_TEXT_MX_ES,
    DEFAULT_PRIVACY_TEXT_MX_EN,
    DEFAULT_ARCO_TEXT_ES,
    DEFAULT_ARCO_TEXT_EN
} from '../../../utilities/BL/PersonalRoulette';

// Fases de la experiencia del aficionado
const PHASES = {
    LOADING: 'LOADING',
    COURTESY: 'COURTESY',
    REGISTER: 'REGISTER',
    ALREADY_PARTICIPATED: 'ALREADY_PARTICIPATED',
    READY_TO_SPIN: 'READY_TO_SPIN'
};

/**
 * COMPONENTE FAN MÓVIL: RULETA BAJA CUP (PersonalRouletteFan)
 * Experiencia interactiva SVG GPU con sintetizador Web Audio API,
 * respuesta háptica, controles flotantes bilingües y parametrización regional (MX / USA).
 */
export const PersonalRouletteFan = ({ roomId: propRoomId, config: propConfig }) => {
    // 1. Detección de Sala y Fuente de Tráfico
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const [activeRoomId, setActiveRoomId] = useState(() => propRoomId || searchParams.get('roomId') || localStorage.getItem('active_room_id') || '');
    const trafficSource = searchParams.get('s') || searchParams.get('src') || searchParams.get('utm_source') || 'direct';

    // 2. Estados de la Dinámica y Configuración
    const [config, setConfig] = useState(() => ({
        region: 'MX',
        titleEs: 'Con Xolos',
        titleEn: 'With Xolos',
        subtitleEs: '¡Registra tus datos y gira para ganar premios exclusivos!',
        subtitleEn: 'Register your info and spin to win exclusive prizes!',
        defaultLanguage: 'es',
        primaryColor: '#dc2626',
        backgroundColor: '#0f172a',
        backgroundImageUrl: '',
        logoUrl: '',
        sponsorLogoUrl: '',
        folioPrefix: 'TJ26',
        winProbability: 35,
        isDynamicActive: true,
        allowMultipleParticipantsOnSameDevice: true,
        spinDurationMs: 5000,
        courtesyMessageEs: 'La dinámica de la ruleta se encuentra en pausa o ha finalizado por hoy. ¡Gracias por tu interés!',
        courtesyMessageEn: 'The roulette dynamic is currently paused or has ended for today. Thank you for your interest!',
        redirectUrl: 'https://www.bajacup.com',
        redirectButtonTextEs: 'Visitar Sitio Oficial',
        redirectButtonTextEn: 'Visit Official Website',
        termsAndConditionsTextEs: DEFAULT_TERMS_TEXT_MX_ES,
        termsAndConditionsTextEn: DEFAULT_TERMS_TEXT_MX_EN,
        privacyPolicyTextEs: DEFAULT_PRIVACY_TEXT_MX_ES,
        privacyPolicyTextEn: DEFAULT_PRIVACY_TEXT_MX_EN,
        startTimeUtc: null,
        endTimeUtc: null,
        prizes: [],
        ...propConfig
    }));

    const isUsaRegion = (config.region || '').toUpperCase() === 'USA';

    // 3. Estado de Idioma (ES / EN) y Diccionario Regional
    const [lang, setLang] = useState(() => detectInitialLanguage(propConfig?.defaultLanguage));
    const t = useMemo(() => getDictionary(lang, config.region || 'MX'), [lang, config.region]);

    // 4. Audio y Silencio
    const [isMuted, setIsMuted] = useState(() => rouletteAudioEngine.getMuted());

    // 5. Fase de la Dinámica
    const [phase, setPhase] = useState(PHASES.LOADING);

    // 5. Formulario de Registro
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        zipCode: '',
        acceptedTerms: false,
        acceptedPrivacyCCPA: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 6. Datos de Participante y Sesión OTT
    const [participantData, setParticipantData] = useState(null);

    // 7. Modales Legales ('terms' | 'privacy' | 'doNotSell' | null)
    const [activeModal, setActiveModal] = useState(null);

    // 8. Notificaciones Toast Flotantes
    const [toastMessage, setToastMessage] = useState(null);
    const toastTimeoutRef = useRef(null);

    // 9. Telemetría de interacción
    const formStartTrackedRef = useRef(false);

    // 10. Estados del Motor SVG y Animación GPU de Ruleta
    const [rotationAngle, setRotationAngle] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [spinDurationMs, setSpinDurationMs] = useState(5000);
    const [isPointerFlicking, setIsPointerFlicking] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const spinAnimFrameRef = useRef(null);
    const spinTimeoutRef = useRef(null);

    // 11. Estado de Generación y Descarga de Cupón Canvas PNG (HU-03)
    const [isGeneratingVoucher, setIsGeneratingVoucher] = useState(false);

    // Dimensiones de ventana para confeti
    const [windowDimensions, setWindowDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 400,
        height: typeof window !== 'undefined' ? window.innerHeight : 800
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sectores activos calculados (de la configuración o fallback)
    const activePrizes = useMemo(() => {
        if (config.prizes && Array.isArray(config.prizes) && config.prizes.length >= 2) {
            return config.prizes;
        }
        return getDefaultPrizes(lang);
    }, [config.prizes, lang]);

    const totalSlices = activePrizes.length;
    const sliceAngle = useMemo(() => calculateSliceAngle(totalSlices), [totalSlices]);
    const pegs = useMemo(() => calculatePegsPositions(250, 250, 226, totalSlices), [totalSlices]);

    // =========================================================================
    // TELEMETRÍA Y SEGUIMIENTO DEL EMBUDO
    // =========================================================================
    const trackEvent = useCallback(async (eventName, overrideRoomId = null) => {
        const effectiveRoomId = overrideRoomId || activeRoomId || localStorage.getItem('active_room_id') || '';
        const sessId = getOrCreateSessionId();
        try {
            await TrackMetricBL(effectiveRoomId, eventName, sessId, trafficSource);
        } catch (e) {
            console.warn('Error en telemetría local de ruleta:', e);
        }
    }, [activeRoomId, trafficSource]);

    const handleFormStart = useCallback(() => {
        if (!formStartTrackedRef.current) {
            formStartTrackedRef.current = true;
            sessionStorage.setItem('personal_roulette_form_start_tracked', 'true');
            trackEvent('form_start');
        }
    }, [trackEvent]);

    const showToast = useCallback((msg, type = 'info', durationMs = 4000) => {
        if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        setToastMessage({ msg, type });
        toastTimeoutRef.current = setTimeout(() => {
            setToastMessage(null);
        }, durationMs);
    }, []);

    // Control de Audio (Mute / Unmute)
    const handleToggleAudio = () => {
        rouletteAudioEngine.initContext();
        const nextMuted = rouletteAudioEngine.toggleMuted();
        setIsMuted(nextMuted);
        showToast(
            nextMuted ? t.toast.soundMuted : t.toast.soundEnabled,
            'info',
            2500
        );
    };

    // Disparos iniciales al montar si activeRoomId ya existe
    useEffect(() => {
        if (activeRoomId) {
            if (!sessionStorage.getItem('personal_roulette_page_view_tracked')) {
                sessionStorage.setItem('personal_roulette_page_view_tracked', 'true');
                trackEvent('page_view', activeRoomId);
            }

            if ((searchParams.has('s') || searchParams.has('src') || searchParams.has('utm_source')) &&
                !sessionStorage.getItem('personal_roulette_qr_scan_tracked')) {
                sessionStorage.setItem('personal_roulette_qr_scan_tracked', 'true');
                trackEvent('qr_scan', activeRoomId);
            }
        }
    }, [activeRoomId, trackEvent, searchParams]);

    // =========================================================================
    // VERIFICACIÓN DE VENTANA HORARIA Y ESTADO DE LA SALA
    // =========================================================================
    const checkIsDynamicOpen = useCallback((cfg) => {
        if (!cfg) return false;
        if (cfg.isDynamicActive === false || cfg.isActive === false) return false;

        const now = new Date();
        if (cfg.startTimeUtc) {
            const start = new Date(cfg.startTimeUtc);
            if (now < start) return false;
        }
        if (cfg.endTimeUtc) {
            const end = new Date(cfg.endTimeUtc);
            if (now > end) return false;
        }

        return true;
    }, []);

    // =========================================================================
    // CARGA DE CONFIGURACIÓN Y SESIÓN LOCAL
    // =========================================================================
    const loadConfigAndSession = useCallback(async () => {
        try {
            const res = await GetPublicConfigBL(activeRoomId);
            let mergedConfig = config;

            if (res.success && res.data) {
                mergedConfig = { ...config, ...res.data };
                setConfig(mergedConfig);

                const resolvedRoomId = res.data.roomId || activeRoomId;
                if (resolvedRoomId) {
                    if (resolvedRoomId !== activeRoomId) {
                        setActiveRoomId(resolvedRoomId);
                    }
                    try {
                        localStorage.setItem('active_room_id', resolvedRoomId);
                    } catch (e) {}

                    // Disparos automáticos de telemetría asegurados con la sala resuelta
                    if (!sessionStorage.getItem('personal_roulette_page_view_tracked')) {
                        sessionStorage.setItem('personal_roulette_page_view_tracked', 'true');
                        trackEvent('page_view', resolvedRoomId);
                    }

                    if ((searchParams.has('s') || searchParams.has('src') || searchParams.has('utm_source')) &&
                        !sessionStorage.getItem('personal_roulette_qr_scan_tracked')) {
                        sessionStorage.setItem('personal_roulette_qr_scan_tracked', 'true');
                        trackEvent('qr_scan', resolvedRoomId);
                    }
                }

                if (res.data.defaultLanguage && !sessionStorage.getItem('personal_roulette_user_lang')) {
                    setLang(res.data.defaultLanguage);
                }

                // Precarga asíncrona de recursos gráficos en background
                preloadAssets([
                    mergedConfig.backgroundImageUrl,
                    mergedConfig.logoUrl,
                    mergedConfig.sponsorLogoUrl,
                    ...(mergedConfig.prizes || []).map(p => p.iconUrl).filter(Boolean)
                ]);
            }

            const isOpen = checkIsDynamicOpen(mergedConfig);
            if (!isOpen) {
                setPhase(PHASES.COURTESY);
                return;
            }

            const stored = getParticipantSession();
            if (stored && stored.participantId) {
                setParticipantData(stored);

                if (stored.status === 'COMPLETED' || stored.status === 'ALREADY_PARTICIPATED' || stored.hasSpun) {
                    setPhase(PHASES.ALREADY_PARTICIPATED);
                    trackEvent('pass_recovered');
                    return;
                }

                if (stored.sessionToken && (stored.status === 'REGISTERED' || stored.status === 'OTT_ACTIVE')) {
                    setPhase(PHASES.READY_TO_SPIN);
                    return;
                }
            }

            setPhase(PHASES.REGISTER);
        } catch (err) {
            console.warn('Error al inicializar sesión de ruleta:', err);
            setPhase(PHASES.REGISTER);
        }
    }, [activeRoomId, config, checkIsDynamicOpen, trackEvent, searchParams]);

    useEffect(() => {
        loadConfigAndSession();
    }, [activeRoomId]);

    // =========================================================================
    // SIGNALR: PAUSA/ACTIVACIÓN Y RESTITUCIÓN DE SESIÓN
    // =========================================================================
    useEffect(() => {
        const handleSignalR = (message) => {
            if (!message || !message.type) return;

            const msgRoomId = message.roomId || message.RoomId;
            if (msgRoomId && activeRoomId && String(msgRoomId).toLowerCase() !== String(activeRoomId).toLowerCase()) {
                return;
            }

            const type = message.type.toLowerCase();

            if (type === 'personalroulette:toggleactive' || type === 'personalroulette.toggleactive' || type === 'set.dynamic') {
                const isActive = message.data?.isActive ?? message.data?.isDynamicActive ?? true;
                setConfig(prev => ({ ...prev, isDynamicActive: isActive }));
                if (!isActive) {
                    setPhase(PHASES.COURTESY);
                } else {
                    setPhase(prev => (prev === PHASES.COURTESY ? PHASES.REGISTER : prev));
                }
            }

            if (type === 'personalroulette:resetsession' || type === 'personalrouletteresetsession') {
                const resetData = message.data || {};
                const stored = getParticipantSession();
                const currentId = participantData?.participantId || stored?.participantId;
                const currentEmail = (participantData?.email || stored?.email || formData.email || '').toLowerCase().trim();
                const currentPhone = (participantData?.phone || stored?.phone || formData.phone || '').trim();

                const matchId = resetData.participantId && currentId && String(resetData.participantId).toLowerCase() === String(currentId).toLowerCase();
                const matchEmail = resetData.email && currentEmail && String(resetData.email).toLowerCase() === currentEmail;
                const matchPhone = resetData.phone && currentPhone && String(resetData.phone) === currentPhone;

                if (matchId || matchEmail || matchPhone) {
                    clearParticipantSession();
                    setParticipantData(null);
                    setFormData({
                        fullName: resetData.fullName || '',
                        email: resetData.email || '',
                        phone: resetData.phone || '',
                        zipCode: resetData.zipCode || '',
                        acceptedTerms: false,
                        acceptedPrivacyCCPA: false
                    });
                    setFormErrors({});
                    setShowConfetti(false);
                    setPhase(PHASES.REGISTER);
                    showToast(
                        lang === 'en'
                            ? 'Your turn was restored by the operator! You may register again.'
                            : '¡Tu oportunidad fue restituida por el operador! Puedes registrarte de nuevo.',
                        'success',
                        6000
                    );
                }
            }
        };

        SignalRService.on('message', handleSignalR);
        return () => {
            SignalRService.off('message', handleSignalR);
        };
    }, [activeRoomId, participantData, formData, lang, showToast]);

    // Limpieza de animaciones al desmontar
    useEffect(() => {
        return () => {
            if (spinAnimFrameRef.current) cancelAnimationFrame(spinAnimFrameRef.current);
            if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
        };
    }, []);

    // =========================================================================
    // CAMBIO DE IDIOMA (ES / EN)
    // =========================================================================
    const handleToggleLanguage = (targetLang) => {
        if (targetLang === lang) return;
        setLang(targetLang);
        sessionStorage.setItem('personal_roulette_user_lang', targetLang);

        if (Object.keys(formErrors).length > 0) {
            const { errors } = validateRegistrationForm(formData, targetLang, config.region || 'MX');
            setFormErrors(errors);
        }
    };

    // =========================================================================
    // MANEJO DEL FORMULARIO Y REGISTRO OTT
    // =========================================================================
    const handleInputChange = (e) => {
        handleFormStart();
        const { name, value, type, checked } = e.target;

        let formattedValue = value;
        if (name === 'phone') formattedValue = value.replace(/\D/g, '').slice(0, 10);
        if (name === 'zipCode') formattedValue = value.replace(/\D/g, '').slice(0, 5);

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : formattedValue
        }));

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleInputBlur = (field) => {
        const { errors } = validateRegistrationForm(formData, lang, config.region || 'MX');
        if (errors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: errors[field] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { isValid, errors } = validateRegistrationForm(formData, lang, config.region || 'MX');
        if (!isValid) {
            setFormErrors(errors);
            const firstErr = Object.values(errors)[0];
            showToast(firstErr, 'danger', 4000);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                roomId: activeRoomId,
                fullName: formData.fullName.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.trim(),
                zipCode: formData.zipCode ? formData.zipCode.trim() : '',
                languageCode: lang,
                trafficSource: trafficSource,
                acceptedTerms: formData.acceptedTerms,
                acceptedPrivacyCCPA: formData.acceptedPrivacyCCPA,
                sessionId: getOrCreateSessionId()
            };

            const res = await ParticipateInitBL(payload, lang, config.region || 'MX');

            if (res.success && res.data) {
                const data = res.data;

                if (data.status === 'ALREADY_PARTICIPATED') {
                    const previousRecord = {
                        participantId: data.participantId,
                        fullName: formData.fullName,
                        email: formData.email,
                        phone: formData.phone,
                        zipCode: formData.zipCode,
                        passCode: data.passCode || `#${config.folioPrefix || (isUsaRegion ? 'SD26' : 'TJ26')}-PREV`,
                        prizeTitle: data.prizeTitle || (lang === 'en' ? 'Previous Spin Completed' : 'Tiro Anterior Registrado'),
                        hasRedeemedPrize: Boolean(data.hasRedeemedPrize),
                        playedAt: data.playedAt || new Date().toISOString(),
                        status: 'ALREADY_PARTICIPATED'
                    };
                    setParticipantData(previousRecord);
                    saveParticipantSession(previousRecord);
                    trackEvent('pass_recovered');
                    setPhase(PHASES.ALREADY_PARTICIPATED);
                    return;
                }

                trackEvent('form_complete');
                const sessionInfo = {
                    participantId: data.participantId,
                    sessionToken: data.sessionToken,
                    sessionTokenExpiresAt: data.sessionTokenExpiresAt,
                    passCode: data.passCode || `#${config.folioPrefix || (isUsaRegion ? 'SD26' : 'TJ26')}-PENDING`,
                    fullName: formData.fullName,
                    email: formData.email,
                    phone: formData.phone,
                    zipCode: formData.zipCode,
                    status: 'REGISTERED'
                };

                setParticipantData(sessionInfo);
                saveParticipantSession(sessionInfo);

                showToast(
                    lang === 'en'
                        ? 'Registration completed!'
                        : '¡Registro exitoso!',
                    'success',
                    3000
                );

                setPhase(PHASES.READY_TO_SPIN);
            } else {
                showToast(res.message || t.errors.genericNetwork, 'danger', 4500);
            }
        } catch (err) {
            console.error('Error al enviar registro:', err);
            showToast(t.errors.genericNetwork, 'danger', 4500);
        } finally {
            setIsSubmitting(false);
        }
    };

    // =========================================================================
    // MOTOR DE GIRO SVG GPU + WEB AUDIO API + HÁPTICOS (HU-02)
    // =========================================================================
    const handleSpinWheel = async () => {
        if (isSpinning) return;
        if (!participantData || !participantData.sessionToken) {
            showToast(t.errors.genericNetwork, 'danger', 4000);
            return;
        }

        // 1. Desbloquear contexto de audio en el primer toque del aficionado
        rouletteAudioEngine.initContext();

        try {
            // 2. Disparar petición segura de giro a backend (o fallback local resiliente)
            const spinPayload = {
                roomId: activeRoomId,
                participantId: participantData.participantId,
                sessionToken: participantData.sessionToken,
                passCode: participantData.passCode
            };

            const spinRes = await SpinRouletteBL(spinPayload, config, lang);

            if (!spinRes.success || !spinRes.data) {
                showToast(spinRes.message || 'Error al procesar el giro.', 'danger', 4000);
                return;
            }

            const {
                targetSliceIndex,
                isWinner,
                prizeText,
                prize,
                spinDurationMs: durMs = 5000,
                passCode: returnedPassCode
            } = spinRes.data;

            const duration = Math.max(3500, durMs);
            setSpinDurationMs(duration);

            // 3. Calcular ángulo objetivo con desaceleración y jitter
            const randomJitterRatio = (Math.random() - 0.5) * 0.5;
            const targetAngle = calculateTargetRotation(
                targetSliceIndex,
                totalSlices,
                rotationAngle,
                7,
                randomJitterRatio
            );

            const startAngle = rotationAngle;
            const angleDelta = targetAngle - startAngle;

            // Iniciar rotación GPU CSS3
            setRotationAngle(targetAngle);
            setIsSpinning(true);
            trackEvent('spin_started');

            // 4. Orquestar tics sonoros mecánicos y vibraciones hápticas sincronizadas
            const startTime = performance.now();
            let lastCrossedPeg = Math.floor(startAngle / sliceAngle);

            const runTickEngine = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(1, elapsed / duration);

                // Aproximación de la curva cubic-bezier(0.12, 0.8, 0.2, 1)
                const easeProgress = 1 - Math.pow(1 - progress, 3.6);
                const currentAngle = startAngle + (angleDelta * easeProgress);

                const currentPeg = Math.floor(currentAngle / sliceAngle);
                if (currentPeg > lastCrossedPeg) {
                    lastCrossedPeg = currentPeg;
                    const speedMultiplier = Math.max(0.2, 1 - progress);
                    rouletteAudioEngine.playMechanicalTick(speedMultiplier);
                    triggerHapticTick();

                    setIsPointerFlicking(true);
                    setTimeout(() => setIsPointerFlicking(false), 35);
                }

                if (progress < 1) {
                    spinAnimFrameRef.current = requestAnimationFrame(runTickEngine);
                }
            };

            spinAnimFrameRef.current = requestAnimationFrame(runTickEngine);

            // 5. Al concluir la animación GPU
            spinTimeoutRef.current = setTimeout(() => {
                setIsSpinning(false);

                if (isWinner) {
                    rouletteAudioEngine.playVictoryFanfare();
                    triggerHapticVictory();
                    setShowConfetti(true);
                }

                const updatedRecord = {
                    ...participantData,
                    passCode: returnedPassCode || participantData.passCode,
                    prizeTitle: prizeText,
                    prize: prize,
                    isWinner: Boolean(isWinner),
                    hasRedeemedPrize: false,
                    playedAt: new Date().toISOString(),
                    status: 'COMPLETED'
                };

                setParticipantData(updatedRecord);
                saveParticipantSession(updatedRecord);
                trackEvent('spin_completed');

                setPhase(PHASES.ALREADY_PARTICIPATED);
            }, duration + 300);

        } catch (err) {
            console.error('Error durante el giro de la ruleta:', err);
            setIsSpinning(false);
            showToast('Ocurrió un inconveniente al girar. Intenta nuevamente.', 'danger', 4000);
        }
    };

    // =========================================================================
    // LISTENERS DE CAPTURA DE PANTALLA (HU-03)
    // =========================================================================
    useEffect(() => {
        if (phase === PHASES.ALREADY_PARTICIPATED && participantData && participantData.isWinner) {
            const cleanup = attachScreenshotListeners(
                participantData.participantId,
                participantData.passCode,
                (method) => {
                    showToast(t.toast.screenshotDetected, 'info', 2500);
                },
                activeRoomId
            );
            return cleanup;
        }
    }, [phase, participantData, activeRoomId, t.toast.screenshotDetected, showToast]);

    // =========================================================================
    // GENERACIÓN Y DESCARGA DIRECTA DE CUPÓN CANVAS PNG (1080 x 1350 px) (HU-03)
    // =========================================================================
    const handleSaveToGallery = async () => {
        if (isGeneratingVoucher || !participantData) return;
        setIsGeneratingVoucher(true);

        try {
            // Obtener el Data URL nítido del elemento QRCodeCanvas montado
            const qrCanvas = document.getElementById('personal-roulette-voucher-qr');
            const qrDataUrl = qrCanvas ? qrCanvas.toDataURL('image/png') : null;

            const canvas = await generateVoucherCanvas({
                participantName: participantData.fullName || formData.fullName,
                passCode: participantData.passCode,
                prizeTitle: participantData.prizeTitle,
                prizeIconUrl: participantData.prize?.iconUrl || participantData.prize?.IconUrl,
                logoUrl: config.logoUrl,
                primaryColor: config.primaryColor || '#dc2626',
                qrDataUrl,
                language: lang,
                createdAt: participantData.playedAt
            });

            const cleanCode = (participantData.passCode || 'VOUCHER').replace(/[^a-zA-Z0-9_-]/g, '_');
            const filename = `Cupon_Ruleta_${cleanCode}.png`;

            await downloadCanvasAsPng(canvas, filename);

            showToast(t.toast.voucherDownloaded || t.result.voucherSavedSuccess, 'success', 4500);

            // Reporte silencioso de telemetría de descarga
            TrackActionBL(
                activeRoomId,
                participantData.participantId,
                'DOWNLOAD',
                'BUTTON_DOWNLOAD',
                participantData.passCode
            );
        } catch (err) {
            console.error('Error al generar cupón digital:', err);
            showToast(
                lang === 'en'
                    ? 'Error generating digital voucher. Please try again.'
                    : 'Error al generar el cupón digital. Intenta nuevamente.',
                'danger',
                4000
            );
        } finally {
            setIsGeneratingVoucher(false);
        }
    };

    // Reinicio de formulario para registrar a un acompañante
    const handlePlayWithAnotherPerson = () => {
        clearParticipantSession();
        setParticipantData(null);
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            zipCode: '',
            acceptedTerms: false,
            acceptedPrivacyCCPA: false
        });
        setFormErrors({});
        setShowConfetti(false);
        formStartTrackedRef.current = false;
        setPhase(PHASES.REGISTER);
        showToast(t.toast.resetSuccess, 'info', 3500);
    };

    // Helper de textos dinámicos por idioma
    const currentTitle = lang === 'en' ? (config.titleEn || t.header.defaultTitle) : (config.titleEs || t.header.defaultTitle);
    const currentSubtitle = lang === 'en' ? (config.subtitleEn || t.header.defaultSubtitle) : (config.subtitleEs || t.header.defaultSubtitle);
    const currentCourtesyMessage = lang === 'en'
        ? (config.courtesyMessageEn || t.courtesy.messageDefault)
        : (config.courtesyMessageEs || t.courtesy.messageDefault);
    const currentRedirectButtonText = lang === 'en'
        ? (config.redirectButtonTextEn || t.courtesy.redirectButtonDefault)
        : (config.redirectButtonTextEs || t.courtesy.redirectButtonDefault);

    // Textos de modales legales oficiales completos
    const termsModalText = lang === 'en'
        ? (config.termsAndConditionsTextEn || (isUsaRegion ? DEFAULT_TERMS_TEXT_EN : DEFAULT_TERMS_TEXT_MX_EN))
        : (config.termsAndConditionsTextEs || (isUsaRegion ? DEFAULT_TERMS_TEXT_ES : DEFAULT_TERMS_TEXT_MX_ES));

    const privacyModalText = lang === 'en'
        ? (config.privacyPolicyTextEn || (isUsaRegion ? DEFAULT_PRIVACY_TEXT_EN : DEFAULT_PRIVACY_TEXT_MX_EN))
        : (config.privacyPolicyTextEs || (isUsaRegion ? DEFAULT_PRIVACY_TEXT_ES : DEFAULT_PRIVACY_TEXT_MX_ES));

    const doNotSellModalText = isUsaRegion
        ? (lang === 'en' ? DEFAULT_DO_NOT_SELL_TEXT_EN : DEFAULT_DO_NOT_SELL_TEXT_ES)
        : (lang === 'en' ? DEFAULT_ARCO_TEXT_EN : DEFAULT_ARCO_TEXT_ES);

    return (
        <div className="PersonalRouletteFan" style={{ background: 'none' }}>
            {/* Confeti Celebratorio para Ganadores */}
            {showConfetti && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 10050 }}>
                    <Confetti
                        width={windowDimensions.width}
                        height={windowDimensions.height}
                        recycle={false}
                        numberOfPieces={280}
                        gravity={0.22}
                    />
                </div>
            )}

            {/* Capa de Fondo Temática */}
            {config.backgroundImageUrl && (
                <div
                    className="PersonalRouletteFan__BgLayer"
                    style={{ backgroundImage: `url(${config.backgroundImageUrl})`, opacity: '1' }}
                />
            )}

            {/* BOTÓN FLOTANTE SUPERIOR IZQUIERDO DE AUDIO (MUTE / SFX) */}
            <button
                type="button"
                className={`PersonalRouletteFan__AudioToggle ${isMuted ? 'PersonalRouletteFan__AudioToggle--Muted' : ''}`}
                onClick={handleToggleAudio}
                title={isMuted ? (lang === 'en' ? 'Unmute Audio' : 'Activar Sonido') : (lang === 'en' ? 'Mute Audio' : 'Silenciar Sonido')}
                aria-label="Toggle Sound"
            >
                <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`} />
                <span className="PersonalRouletteFan__AudioToggle__Label">
                    {isMuted ? 'MUTE' : 'SFX'}
                </span>
            </button>

            {/* BOTÓN FLOTANTE SUPERIOR DERECHO DE IDIOMA (ES / EN) */}
            <div className="PersonalRouletteFan__LangToggle" title="Cambiar Idioma / Change Language">
                <button
                    type="button"
                    className={`PersonalRouletteFan__LangToggle__Btn ${lang === 'es' ? 'PersonalRouletteFan__LangToggle__Btn--Active' : ''}`}
                    onClick={() => handleToggleLanguage('es')}
                >
                    ES
                </button>
                <button
                    type="button"
                    className={`PersonalRouletteFan__LangToggle__Btn ${lang === 'en' ? 'PersonalRouletteFan__LangToggle__Btn--Active' : ''}`}
                    onClick={() => handleToggleLanguage('en')}
                >
                    EN
                </button>
            </div>

            <div className="PersonalRouletteFan__Container">
                {/* CABECERA TEMÁTICA DEPORTIVA OFICIAL CON ZARPAZOS */}
                <header className="PersonalRouletteFan__Header">
                    {/* Zarpazos Rojos Superiores Derechos */}
                    <div className="PersonalRouletteFan__ClawMarks">
                        <svg viewBox="0 0 160 120" className="PersonalRouletteFan__ClawMarksSvg" aria-hidden="true">
                            <defs>
                                <linearGradient id="clawGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#ff2a4b" />
                                    <stop offset="60%" stopColor="#dc2626" />
                                    <stop offset="100%" stopColor="#7f1d1d" />
                                </linearGradient>
                                <filter id="clawGlow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#dc2626" floodOpacity="0.8" />
                                </filter>
                            </defs>
                            <path d="M 35 6 C 70 16, 120 45, 155 105 C 148 95, 115 42, 45 12 Z" fill="url(#clawGrad)" filter="url(#clawGlow)" />
                            <path d="M 18 28 C 50 38, 95 68, 128 118 C 120 105, 88 56, 26 35 Z" fill="url(#clawGrad)" filter="url(#clawGlow)" />
                            <path d="M 6 52 C 32 62, 70 90, 100 128 C 93 118, 65 78, 12 58 Z" fill="url(#clawGrad)" filter="url(#clawGlow)" />
                        </svg>
                    </div>

                    <div className="PersonalRouletteFan__LogosWrapper">
                        {config.logoUrl ? (
                            <img src={config.logoUrl} alt="Logo Oficial" className="PersonalRouletteFan__Logo" style={{ maxWidth: '250px' }} />
                        ) : (
                            <div className="PersonalRouletteFan__OfficialBadge" title="Club Tijuana">
                                <svg viewBox="0 0 100 100" className="PersonalRouletteFan__OfficialBadgeSvg">
                                    <circle cx="50" cy="50" r="48" fill="#ffffff" stroke="#171717" strokeWidth="3" />
                                    <circle cx="50" cy="50" r="42" fill="#171717" />
                                    <circle cx="50" cy="50" r="32" fill="#dc2626" />
                                    <path d="M 38 65 L 42 42 L 32 30 L 45 35 L 50 20 L 55 35 L 68 30 L 58 42 L 62 65 L 50 58 Z" fill="#ffffff" />
                                    <circle cx="45" cy="45" r="2.5" fill="#171717" />
                                    <circle cx="55" cy="45" r="2.5" fill="#171717" />
                                </svg>
                            </div>
                        )}
                        {config.sponsorLogoUrl && (
                            <img src={config.sponsorLogoUrl} alt="Sponsor Logo" className="PersonalRouletteFan__SponsorLogo" />
                        )}
                    </div>

                    {/* Título Atlético en 2 Tonos (Blanco Impacto + Rojo Slant) */}
                    <div className="PersonalRouletteFan__AthleticTitleGroup">
                        <span className="PersonalRouletteFan__TitleLine1">¡GIRA Y GANA</span>
                        <span className="PersonalRouletteFan__TitleLine2" style={{ color: 'white' }}>
                            {currentTitle && currentTitle.toLowerCase().includes('xolos')
                                ? 'CON XOLOS!'
                                : (currentTitle.toUpperCase() || 'CON XOLOS!')}
                        </span>
                    </div>

                    <p className="PersonalRouletteFan__Subtitle">
                        {currentSubtitle || (lang === 'en' ? 'REGISTER, SPIN THE WHEEL AND WIN AMAZING PRIZES.' : 'REGÍSTRATE, GIRA LA RULETA Y LLÉVATE PREMIOS INCREÍBLES.')}
                    </p>
                </header>

                {/* ============================================================= */}
                {/* 1. PANTALLA DE CORTESÍA (PAUSA O FUERA DE HORARIO)           */}
                {/* ============================================================= */}
                {phase === PHASES.COURTESY && (
                    <div className="PersonalRouletteFan__Card PersonalRouletteFan__CourtesyCard">
                        <div className="PersonalRouletteFan__CourtesyIcon">
                            <i className="bi bi-pause-circle-fill" />
                        </div>
                        <span className="PersonalRouletteFan__CourtesyBadge">
                            {t.courtesy.badge}
                        </span>
                        <h3 className="PersonalRouletteFan__Title" style={{ fontSize: '1.3rem', marginBottom: '0.8rem' }}>
                            {t.courtesy.title}
                        </h3>
                        <p className="PersonalRouletteFan__CourtesyMessage">
                            {currentCourtesyMessage}
                        </p>
                        {config.redirectUrl && (
                            <a
                                href={config.redirectUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="PersonalRouletteFan__RedirectBtn"
                            >
                                <i className="bi bi-box-arrow-up-right" /> {currentRedirectButtonText}
                            </a>
                        )}
                    </div>
                )}

                {/* ============================================================= */}
                {/* 2. FORMULARIO DE REGISTRO BILINGÜE MÓVIL (HU-01)              */}
                {/* ============================================================= */}
                {phase === PHASES.REGISTER && (
                    <div className="PersonalRouletteFan__Card">
                        <form className="PersonalRouletteFan__Form" onSubmit={handleSubmit} noValidate>
                            {/* Campo 1: Nombre Completo */}
                            <div className="PersonalRouletteFan__FormGroup">
                                <label className="PersonalRouletteFan__Label" htmlFor="fullName">
                                    <i style={{ color: 'red' }} className="bi bi-person-fill" /> {t.form.fullNameLabel}
                                </label>
                                <div className="PersonalRouletteFan__InputWrapper">
                                    <input
                                        id="fullName"
                                        name="fullName"
                                        type="text"
                                        className={`PersonalRouletteFan__Input ${formErrors.fullName ? 'PersonalRouletteFan__Input--Error' : ''}`}
                                        placeholder={t.form.fullNamePlaceholder}
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        onBlur={() => handleInputBlur('fullName')}
                                        autoComplete="name"
                                        maxLength={80}
                                    />
                                    <span className="PersonalRouletteFan__InputIcon">
                                        <i className="bi bi-person" />
                                    </span>
                                </div>
                                {formErrors.fullName && (
                                    <span className="PersonalRouletteFan__ErrorMessage">
                                        <i className="bi bi-exclamation-circle-fill" /> {formErrors.fullName}
                                    </span>
                                )}
                            </div>

                            {/* Campo 2: Correo Electrónico */}
                            <div className="PersonalRouletteFan__FormGroup">
                                <label className="PersonalRouletteFan__Label" htmlFor="email">
                                    <i style={{ color: 'red' }} className="bi bi-envelope-fill" /> {t.form.emailLabel}
                                </label>
                                <div className="PersonalRouletteFan__InputWrapper">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        className={`PersonalRouletteFan__Input ${formErrors.email ? 'PersonalRouletteFan__Input--Error' : ''}`}
                                        placeholder={t.form.emailPlaceholder}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        onBlur={() => handleInputBlur('email')}
                                        autoComplete="email"
                                        maxLength={100}
                                    />
                                    <span className="PersonalRouletteFan__InputIcon">
                                        <i className="bi bi-envelope" />
                                    </span>
                                </div>
                                {formErrors.email && (
                                    <span className="PersonalRouletteFan__ErrorMessage">
                                        <i className="bi bi-exclamation-circle-fill" /> {formErrors.email}
                                    </span>
                                )}
                            </div>

                            {/* Campo 3: Teléfono Celular */}
                            <div className="PersonalRouletteFan__FormGroup">
                                <label className="PersonalRouletteFan__Label" htmlFor="phone">
                                    <i style={{ color: 'red' }} className="bi bi-telephone-fill" /> {t.form.phoneLabel}
                                </label>
                                <div className="PersonalRouletteFan__InputWrapper">
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        className={`PersonalRouletteFan__Input ${formErrors.phone ? 'PersonalRouletteFan__Input--Error' : ''}`}
                                        placeholder={t.form.phonePlaceholder}
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        onBlur={() => handleInputBlur('phone')}
                                        autoComplete="tel"
                                        maxLength={10}
                                    />
                                    <span className="PersonalRouletteFan__InputIcon">
                                        <i className="bi bi-phone" />
                                    </span>
                                </div>
                                {formErrors.phone && (
                                    <span className="PersonalRouletteFan__ErrorMessage">
                                        <i className="bi bi-exclamation-circle-fill" /> {formErrors.phone}
                                    </span>
                                )}
                            </div>

                            {/* Campo 4: Código Postal (Solo requerido y visible en región USA) */}
                            {isUsaRegion && (
                                <div className="PersonalRouletteFan__FormGroup">
                                    <label className="PersonalRouletteFan__Label" htmlFor="zipCode">
                                        <i style={{ color: 'red' }} className="bi bi-geo-alt-fill" /> {t.form.zipCodeLabel}
                                    </label>
                                    <div className="PersonalRouletteFan__InputWrapper">
                                        <input
                                            id="zipCode"
                                            name="zipCode"
                                            type="text"
                                            className={`PersonalRouletteFan__Input ${formErrors.zipCode ? 'PersonalRouletteFan__Input--Error' : ''}`}
                                            placeholder={t.form.zipCodePlaceholder}
                                            value={formData.zipCode}
                                            onChange={handleInputChange}
                                            onBlur={() => handleInputBlur('zipCode')}
                                            autoComplete="postal-code"
                                            maxLength={5}
                                        />
                                        <span className="PersonalRouletteFan__InputIcon">
                                            <i className="bi bi-pin-map" />
                                        </span>
                                    </div>
                                    {formErrors.zipCode && (
                                        <span className="PersonalRouletteFan__ErrorMessage">
                                            <i className="bi bi-exclamation-circle-fill" /> {formErrors.zipCode}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Checkboxes Legales CCPA */}
                            <div className="PersonalRouletteFan__CheckboxGroup">
                                <div className={formErrors.acceptedTerms ? 'PersonalRouletteFan__CheckboxLabel--Error' : ''}>
                                    <label className="PersonalRouletteFan__CheckboxLabel">
                                        <input
                                            type="checkbox"
                                            name="acceptedTerms"
                                            checked={formData.acceptedTerms}
                                            onChange={handleInputChange}
                                        />
                                        <span>
                                            {t.form.checkboxTerms}{' '}
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                className="PersonalRouletteFan__LegalLink"
                                                onClick={(ev) => {
                                                    ev.preventDefault();
                                                    ev.stopPropagation();
                                                    setActiveModal('terms');
                                                }}
                                                onKeyDown={(ev) => ev.key === 'Enter' && setActiveModal('terms')}
                                            >
                                                [{t.form.termsLinkText}]
                                            </span>
                                        </span>
                                    </label>
                                    {formErrors.acceptedTerms && (
                                        <span className="PersonalRouletteFan__ErrorMessage" style={{ marginLeft: '30px' }}>
                                            <i className="bi bi-exclamation-circle-fill" /> {formErrors.acceptedTerms}
                                        </span>
                                    )}
                                </div>

                                <div className={formErrors.acceptedPrivacyCCPA ? 'PersonalRouletteFan__CheckboxLabel--Error' : ''}>
                                    <label className="PersonalRouletteFan__CheckboxLabel">
                                        <input
                                            type="checkbox"
                                            name="acceptedPrivacyCCPA"
                                            checked={formData.acceptedPrivacyCCPA}
                                            onChange={handleInputChange}
                                        />
                                        <span>
                                            {t.form.checkboxPrivacy}{' '}
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                className="PersonalRouletteFan__LegalLink"
                                                onClick={(ev) => {
                                                    ev.preventDefault();
                                                    ev.stopPropagation();
                                                    setActiveModal('privacy');
                                                }}
                                                onKeyDown={(ev) => ev.key === 'Enter' && setActiveModal('privacy')}
                                            >
                                                [{t.form.privacyLinkText}]
                                            </span>
                                        </span>
                                    </label>
                                    {formErrors.acceptedPrivacyCCPA && (
                                        <span className="PersonalRouletteFan__ErrorMessage" style={{ marginLeft: '30px' }}>
                                            <i className="bi bi-exclamation-circle-fill" /> {formErrors.acceptedPrivacyCCPA}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Enlace CCPA Opt-Out */}
                            <div className="PersonalRouletteFan__DoNotSellWrapper">
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className="PersonalRouletteFan__DoNotSellLink"
                                    onClick={() => setActiveModal('doNotSell')}
                                    onKeyDown={(ev) => ev.key === 'Enter' && setActiveModal('doNotSell')}
                                >
                                    <i className="bi bi-shield-lock-fill" /> {t.form.doNotSellLink}
                                </span>
                            </div>

                            {/* Botón de Envío */}
                            <button
                                type="submit"
                                className="PersonalRouletteFan__SubmitBtn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        <span>{t.form.submittingButton}</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-arrow-right-circle-fill" />
                                        <span>{t.form.submitButton}</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}

                {/* ============================================================= */}
                {/* 3. MOTOR VECTORIAL SVG DEPORTIVO: READY_TO_SPIN (HU-02, HU-12) */}
                {/* ============================================================= */}
                {phase === PHASES.READY_TO_SPIN && participantData && (
                    <div className="PersonalRouletteFan__Card PersonalRouletteFan__WheelCard" style={{ backgroundColor: 'transparent', boxShadow: 'none', border: '0' }}>
                        {/* Contenedor SVG de la Ruleta Deportiva */}
                        <div className="PersonalRouletteFan__WheelStage">
                            {/* Puntero Superior en Forma de Pin Deportivo Oficial con Logotipo (12:00 / 0°) */}
                            <div className={`PersonalRouletteFan__Pointer ${isPointerFlicking ? 'PersonalRouletteFan__Pointer--Flick' : ''}`}>
                                <svg viewBox="0 0 54 74" className="PersonalRouletteFan__PointerSvg" aria-hidden="true">
                                    <defs>
                                        <linearGradient id="pointerRedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#ff3355" />
                                            <stop offset="45%" stopColor="#dc2626" />
                                            <stop offset="100%" stopColor="#881313" />
                                        </linearGradient>
                                        <linearGradient id="silverRimGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#ffffff" />
                                            <stop offset="35%" stopColor="#e2e8f0" />
                                            <stop offset="70%" stopColor="#94a3b8" />
                                            <stop offset="100%" stopColor="#475569" />
                                        </linearGradient>
                                        <filter id="pointerRealShadow" x="-30%" y="-30%" width="160%" height="160%">
                                            <feDropShadow dx="0" dy="6" stdDeviation="5" floodColor="#000000" floodOpacity="0.85" />
                                        </filter>
                                    </defs>
                                    {/* Pin de ubicación deportivo con bisel plateado */}
                                    <g filter="url(#pointerRealShadow)">
                                        <path
                                            d="M 27 2 C 12 2, 0 14, 0 28 C 0 44, 27 72, 27 72 C 27 72, 54 44, 54 28 C 54 14, 42 2, 27 2 Z"
                                            fill="url(#silverRimGrad)"
                                        />
                                        <path
                                            d="M 27 5 C 14.5 5, 4 15.5, 4 28 C 4 41.5, 27 66, 27 66 C 27 66, 50 41.5, 50 28 C 50 15.5, 39.5 5, 27 5 Z"
                                            fill="url(#pointerRedGrad)"
                                        />
                                        {/* Insignia central del pin */}
                                        <circle cx="27" cy="27" r="16" fill="#ffffff" stroke="#94a3b8" strokeWidth="1.2" />
                                        {config.logoUrl ? (
                                            <image
                                                href={config.logoUrl}
                                                x="15"
                                                y="15"
                                                width="24"
                                                height="24"
                                                preserveAspectRatio="xMidYMid meet"
                                            />
                                        ) : (
                                            <polygon
                                                points="27,15 30,23 38,24 32,30 34,38 27,34 20,38 22,30 16,24 24,23"
                                                fill="#dc2626"
                                            />
                                        )}
                                        {/* Destello de luz superior */}
                                        <ellipse cx="23" cy="14" rx="8" ry="4" fill="#ffffff" opacity="0.4" transform="rotate(-25 23 14)" />
                                    </g>
                                </svg>
                            </div>

                            {/* SVG de la Ruleta Deportiva con Bisel Metálico Titanio y Remaches 3D */}
                            <svg
                                viewBox="0 0 500 500"
                                className="PersonalRouletteFan__WheelSvg"
                                aria-label="Ruleta deportiva interactiva"
                            >
                                <defs>
                                    {/* Gradiente de Chasis Titanio Oscuro */}
                                    <radialGradient id="titaniumBezelRim" cx="50%" cy="50%" r="50%">
                                        <stop offset="82%" stopColor="#0a0e17" />
                                        <stop offset="89%" stopColor="#1e293b" />
                                        <stop offset="93%" stopColor="#334155" />
                                        <stop offset="97%" stopColor="#1e293b" />
                                        <stop offset="100%" stopColor="#0f172a" />
                                    </radialGradient>

                                    {/* Gradiente Cromado de Alta Definición */}
                                    <linearGradient id="chromeBezelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#ffffff" />
                                        <stop offset="25%" stopColor="#94a3b8" />
                                        <stop offset="50%" stopColor="#334155" />
                                        <stop offset="75%" stopColor="#e2e8f0" />
                                        <stop offset="100%" stopColor="#1e293b" />
                                    </linearGradient>

                                    {/* Gradiente de Remaches 3D Plateados */}
                                    <radialGradient id="silverPegGrad" cx="35%" cy="35%" r="65%">
                                        <stop offset="0%" stopColor="#ffffff" />
                                        <stop offset="35%" stopColor="#e2e8f0" />
                                        <stop offset="70%" stopColor="#94a3b8" />
                                        <stop offset="100%" stopColor="#334155" />
                                    </radialGradient>

                                    {/* Filtros de Sombra y Brillo */}
                                    <filter id="hubDropShadow" x="-30%" y="-30%" width="160%" height="160%">
                                        <feDropShadow dx="0" dy="6" stdDeviation="7" floodColor="#000000" floodOpacity="0.85" />
                                    </filter>

                                    <filter id="textGlow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="#000000" floodOpacity="0.95" />
                                    </filter>

                                    <filter id="spokeGlow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="0" dy="0" stdDeviation="1.5" floodColor="#ffffff" floodOpacity="0.6" />
                                    </filter>
                                </defs>

                                {/* 1. Aro Exterior Titanio Fijo de Protección */}
                                <circle cx="250" cy="250" r="246" fill="url(#titaniumBezelRim)" stroke="#475569" strokeWidth="2.5" />
                                <circle cx="250" cy="250" r="222" fill="none" stroke="#1e293b" strokeWidth="2" />

                                {/* 2. GRUPO ROTATIVO ACCIONADO POR GPU CSS3 */}
                                <g
                                    className="PersonalRouletteFan__WheelRotateGroup"
                                    style={{
                                        transform: `rotate(${rotationAngle}deg)`,
                                        transformOrigin: '250px 250px',
                                        transition: isSpinning
                                            ? `transform ${spinDurationMs}ms cubic-bezier(0.12, 0.8, 0.2, 1)`
                                            : 'none'
                                    }}
                                >
                                    {/* Sectores / Rebanadas (2 a 12) */}
                                    {activePrizes.map((prize, idx) => {
                                        const startAngle = idx * sliceAngle;
                                        const endAngle = (idx + 1) * sliceAngle;
                                        const midAngle = (startAngle + endAngle) / 2;
                                        const arcPath = describeArc(250, 250, 218, startAngle, endAngle);

                                        const prizeTitle = lang === 'en'
                                            ? (prize.textEn || prize.TextEn || prize.textEs || prize.TextEs || `Prize ${idx + 1}`)
                                            : (prize.textEs || prize.TextEs || prize.textEn || prize.TextEn || `Premio ${idx + 1}`);

                                        // Formateo de texto en dos líneas
                                        const words = prizeTitle.toUpperCase().split(' ');
                                        const line1 = words.length > 2 ? words.slice(0, Math.ceil(words.length / 2)).join(' ') : (words[0] || '');
                                        const line2 = words.length > 2 ? words.slice(Math.ceil(words.length / 2)).join(' ') : (words.slice(1).join(' ') || '');

                                        // Colores Alternados Oficiales: Rojo Carmesí (#c8102e) y Negro Carbón (#141414)
                                        const defaultBg = idx % 2 === 0 ? '#c8102e' : '#141414';
                                        const bgColor = prize.backgroundColor || prize.BackgroundColor || defaultBg;
                                        const txtColor = prize.textColor || prize.TextColor || '#ffffff';

                                        return (
                                            <g key={`slice-${prize.id || idx}`}>
                                                {/* Rebanada con borde nítido */}
                                                <path
                                                    d={arcPath}
                                                    fill={bgColor}
                                                    stroke="#ffffff"
                                                    strokeWidth="2"
                                                    filter="url(#spokeGlow)"
                                                />

                                                {/* Contenido Radial (Texto, Imagen HD y Estrella centrados) */}
                                                <g transform={`rotate(${midAngle}, 250, 250)`}>
                                                    {/* Texto Exterior en Blanco Negrita */}
                                                    <text
                                                        x="250"
                                                        y={line2 ? '68' : '76'}
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        fill={txtColor}
                                                        // fontSize={totalSlices > 8 ? '10' : (totalSlices > 6 ? '11' : '12.5')}
                                                        fontSize={totalSlices > 8 ? '10' : (totalSlices > 6 ? '11' : '16')}
                                                        fontWeight="900"
                                                        letterSpacing="0.8px"
                                                        filter="url(#textGlow)"
                                                    >
                                                        {line1}
                                                        {line2 && (
                                                            // <tspan x="250" dy="13" fontSize={totalSlices > 8 ? '9' : '10.5'} fontWeight="800">
                                                            <tspan x="250" dy="13" fontSize={totalSlices > 8 ? '9' : '16'} fontWeight="800">
                                                                {line2}
                                                            </tspan>
                                                        )}
                                                    </text>

                                                    {/* Imagen HD Realista del Premio */}
                                                    {prize.iconUrl || prize.IconUrl && (
                                                        <image
                                                            href={prize.iconUrl || prize.IconUrl}
                                                            x="226"
                                                            y="108"
                                                            width="48"
                                                            height="48"
                                                            preserveAspectRatio="xMidYMid meet"
                                                        />
                                                    )}
                                                    {/* //: (
                                                    //     <g transform="translate(250, 132)">
                                                    //         <rect x="-16" y="-16" width="32" height="32" rx="6" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                                                    //         <text x="0" y="5" textAnchor="middle" fontSize="16" fill="#ffffff">🎁</text>
                                                    //     </g>
                                                    // )} */}

                                                    {/* Estrella Deportiva Decorativa en la Cúspide cerca del Hub */}
                                                    {/* <polygon
                                                        points="250,172 252.5,178 259,178.5 254,183 255.5,189.5 250,186 244.5,189.5 246,183 241,178.5 247.5,178"
                                                        fill="#dc2626"
                                                        stroke="#ffffff"
                                                        strokeWidth="0.8"
                                                    /> */}
                                                </g>
                                            </g>
                                        );
                                    })}

                                    {/* Remaches Perimetrales 3D (Pegs) Plateados */}
                                    {pegs.map((peg) => (
                                        <g key={`peg-${peg.index}`}>
                                            <circle
                                                cx={peg.x}
                                                cy={peg.y}
                                                r="6.5"
                                                fill="url(#silverPegGrad)"
                                                stroke="#1e293b"
                                                strokeWidth="1.2"
                                            />
                                            <circle
                                                cx={peg.x - 2}
                                                cy={peg.y - 2}
                                                r="1.8"
                                                fill="#ffffff"
                                                opacity="0.95"
                                            />
                                        </g>
                                    ))}

                                    {/* 3. Hub Central Circular con Bisel Cromado y Escudo Oficial */}
                                    <g filter="url(#hubDropShadow)">
                                        {/* Bisel exterior cromado plateado */}
                                        <circle cx="250" cy="250" r="50" fill="url(#chromeBezelGrad)" stroke="#475569" strokeWidth="2.5" />
                                        {/* Disco interior negro / blanco */}
                                        <circle cx="250" cy="250" r="40" fill="#171717" stroke="#ffffff" strokeWidth="2" />

                                        {/* Logotipo o Escudo Oficial en el Centro */}
                                        {config.logoUrl ? (
                                            <image
                                                href={config.logoUrl}
                                                x="216"
                                                y="216"
                                                width="68"
                                                height="68"
                                                preserveAspectRatio="xMidYMid meet"
                                            />
                                        ) : (
                                            <g transform="translate(250, 250) scale(0.72)">
                                                <circle cx="0" cy="0" r="42" fill="#ffffff" />
                                                <circle cx="0" cy="0" r="36" fill="#dc2626" />
                                                <path d="M -12 18 L -8 -4 L -18 -16 L -5 -11 L 0 -26 L 5 -11 L 18 -16 L 8 -4 L 12 18 L 0 11 Z" fill="#ffffff" />
                                            </g>
                                        )}
                                    </g>
                                </g>
                            </svg>
                        </div>

                        {/* Botón de Acción Principal "Girar Ruleta" */}
                        <div className="PersonalRouletteFan__SpinActionWrapper">
                            <button
                                type="button"
                                className={`PersonalRouletteFan__SpinBtn ${!isSpinning ? 'PersonalRouletteFan__SpinBtn--Pulse' : 'PersonalRouletteFan__SpinBtn--Spinning'}`}
                                onClick={handleSpinWheel}
                                disabled={isSpinning}
                            >
                                {isSpinning ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        <span>{t.readyToSpin.spinningButton}</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-play-circle-fill" />
                                        <span>{t.readyToSpin.spinButton}</span>
                                    </>
                                )}
                            </button>
                            <p className="PersonalRouletteFan__SpinHint">
                                {isSpinning ? (lang === 'en' ? 'Good luck!' : '¡Mucha suerte!') : t.readyToSpin.spinInstruction}
                            </p>
                        </div>

                        {/* Barra Inferior Temática: Hashtag + Badge ¡TODOS GANAN! + Mascota */}
                        {/* <div className="PersonalRouletteFan__AthleticFooterBar">
                            <div className="PersonalRouletteFan__HashtagBadge">
                                #JAURÍA<span className="text-danger">DETIJUANA</span>
                            </div>

                            <div className="PersonalRouletteFan__AllWinBadge">
                                <span className="PersonalRouletteFan__PawIcon">🐾</span>
                                <span className="PersonalRouletteFan__AllWinText">¡TODOS GANAN!</span>
                            </div>

                            <div className="PersonalRouletteFan__MascotIcon" title="Xoloitzcuintle">
                                <svg viewBox="0 0 36 36" className="PersonalRouletteFan__MascotSvg" aria-hidden="true">
                                    <circle cx="18" cy="18" r="16" fill="#171717" stroke="#dc2626" strokeWidth="1.8" />
                                    <path d="M 12 24 L 14 15 L 10 10 L 16 12 L 18 6 L 20 12 L 26 10 L 22 15 L 24 24 L 18 21 Z" fill="#ffffff" />
                                    <circle cx="15" cy="17" r="1" fill="#dc2626" />
                                    <circle cx="21" cy="17" r="1" fill="#dc2626" />
                                </svg>
                            </div>
                        </div> */}
                    </div>
                )}

                {/* ============================================================= */}
                {/* 4. RESULTADO GANADOR: VOUCHER OFICIAL DIGITAL (HU-03)         */}
                {/* ============================================================= */}
                {phase === PHASES.ALREADY_PARTICIPATED && participantData && participantData.isWinner && (
                    <div className="PersonalRouletteFan__Card PersonalRouletteFan__VoucherCard PersonalRouletteFan__VoucherCard--Winner">
                        {/* Insignia de Estado */}
                        <span className="PersonalRouletteFan__ResultBadge PersonalRouletteFan__ResultBadge--Winner">
                            <i className="bi bi-trophy-fill" /> {t.result.winnerBadge}
                        </span>

                        <h3 className="PersonalRouletteFan__Title" style={{ fontSize: '1.5rem', marginBottom: '0.35rem' }}>
                            {t.result.winnerTitle}
                        </h3>
                        <p className="PersonalRouletteFan__Subtitle" style={{ marginBottom: '1.2rem' }}>
                            {t.result.winnerSubtitle}
                        </p>

                        {/* Caja Destacada del Folio Oficial */}
                        {(config.showDigitalFolio !== false && config.ShowDigitalFolio !== false) && (
                            <div className="PersonalRouletteFan__FolioBox">
                                <div className="PersonalRouletteFan__FolioLabel">
                                    {t.result.folioLabel}
                                </div>
                                <div className="PersonalRouletteFan__FolioValue">
                                    {participantData.passCode || `#${config.folioPrefix || 'SD26'}-7742`}
                                </div>
                                {participantData.prizeTitle && (
                                    <div className="PersonalRouletteFan__PrizeTitleBox">
                                        <i className="bi bi-award-fill" /> {participantData.prizeTitle}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Código QR Dinámico Renderizado con QRCodeCanvas para Escaneo de Staff */}
                        {(config.showVoucherQr !== false && config.ShowVoucherQr !== false) && (
                            <div className="PersonalRouletteFan__QrContainer">
                                <div className="PersonalRouletteFan__QrWrapper">
                                    <QRCodeCanvas
                                        id="personal-roulette-voucher-qr"
                                        value={participantData.passCode || 'SD26-VOUCHER'}
                                        size={180}
                                        level="H"
                                        includeMargin={true}
                                        bgColor="#ffffff"
                                        fgColor="#0f172a"
                                    />
                                </div>
                                <span className="PersonalRouletteFan__QrHint">
                                    <i className="bi bi-qr-code-scan" /> {t.result.qrInstructions}
                                </span>
                            </div>
                        )}

                        {/* Botón Principal: Guardar en Galería (Canvas 1080 x 1350 px PNG) */}
                        {(config.showSaveToGalleryButton !== false && config.ShowSaveToGalleryButton !== false) && (
                            <button
                                type="button"
                                className="PersonalRouletteFan__SaveVoucherBtn"
                                onClick={handleSaveToGallery}
                                disabled={isGeneratingVoucher}
                            >
                                {isGeneratingVoucher ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        <span>{t.result.savingVoucher}</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-download" />
                                        <span>{t.result.saveToGalleryBtn}</span>
                                    </>
                                )}
                            </button>
                        )}

                        {/* Desglose de Información */}
                        {(config.showParticipantInfo !== false && config.ShowParticipantInfo !== false) && (
                            <div className="PersonalRouletteFan__ResultDetails">
                                <div className="PersonalRouletteFan__DetailRow">
                                    <span className="PersonalRouletteFan__DetailKey">{t.form.fullNameLabel}:</span>
                                    <span className="PersonalRouletteFan__DetailVal">{participantData.fullName || formData.fullName || '--'}</span>
                                </div>
                                <div className="PersonalRouletteFan__DetailRow">
                                    <span className="PersonalRouletteFan__DetailKey">{t.alreadyParticipated.playedAtLabel}:</span>
                                    <span className="PersonalRouletteFan__DetailVal">
                                        {formatEventDate(participantData.playedAt, lang)}
                                    </span>
                                </div>
                                <div className="PersonalRouletteFan__DetailRow">
                                    <span className="PersonalRouletteFan__DetailKey">{t.alreadyParticipated.statusLabel}:</span>
                                    <span
                                        className="PersonalRouletteFan__DetailVal"
                                        style={{ color: participantData.hasRedeemedPrize ? '#10b981' : '#f59e0b' }}
                                    >
                                        {participantData.hasRedeemedPrize
                                            ? t.result.redeemDone
                                            : t.result.redeemPending}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Instrucciones de Canje en Sitio */}
                        {(config.showRedemptionInstructions !== false && config.ShowRedemptionInstructions !== false) && (
                            <div className="PersonalRouletteFan__InstructionsBox">
                                <h6>
                                    <i className="bi bi-info-circle-fill" /> {t.result.instructionsTitle}
                                </h6>
                                <p>{t.result.instructionsDefault}</p>
                            </div>
                        )}

                        {/* Modo Dispositivo Compartido */}
                        {(config.allowMultipleParticipantsOnSameDevice || config.AllowMultipleParticipantsOnSameDevice) && (
                            <div className="PersonalRouletteFan__SharedDeviceSection">
                                <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '0.3rem' }}>
                                    {t.alreadyParticipated.sharedDeviceNotice}
                                </p>
                                <button
                                    type="button"
                                    className="PersonalRouletteFan__PlayAnotherBtn"
                                    onClick={handlePlayWithAnotherPerson}
                                >
                                    <i className="bi bi-person-plus-fill" /> {t.result.anotherPlayerBtn}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ============================================================= */}
                {/* 5. RESULTADO NO GANADOR: PANTALLA INSTITUCIONAL DE GRACIAS     */}
                {/* ============================================================= */}
                {phase === PHASES.ALREADY_PARTICIPATED && participantData && !participantData.isWinner && (
                    <div className="PersonalRouletteFan__Card PersonalRouletteFan__ThanksCard">
                        {/* Icono Emblema de Agradecimiento */}
                        <div className="PersonalRouletteFan__ThanksIcon">
                            {/* <i className="bi bi-patch-check-fill" /> */}
                            <i className="bi bi-x-circle" />
                        </div>

                        {/* Insignia Pase Conmemorativo */}
                        <span className="PersonalRouletteFan__ResultBadge">
                            <i className="bi bi-award" /> {t.result.courtesyBadge}
                        </span>

                        <h3 className="PersonalRouletteFan__Title" style={{ fontSize: '1.45rem', marginBottom: '0.35rem' }}>
                            {t.result.thanksTitle}
                        </h3>
                        <p className="PersonalRouletteFan__Subtitle" style={{ marginBottom: '1.2rem' }}>
                            {t.result.thanksSubtitle}
                        </p>

                        {/* <p className="PersonalRouletteFan__ThanksMessage">
                            {currentCourtesyMessage || t.result.thanksMessage}
                        </p> */}

                        {/* Modo Dispositivo Compartido */}
                        {(config.allowMultipleParticipantsOnSameDevice || config.AllowMultipleParticipantsOnSameDevice) && (
                            <div className="PersonalRouletteFan__SharedDeviceSection" style={{ marginTop: '1.5rem' }}>
                                <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.4rem' }}>
                                    {t.alreadyParticipated.sharedDeviceNotice}
                                </p>
                                <button
                                    type="button"
                                    className="PersonalRouletteFan__PlayAnotherBtn"
                                    onClick={handlePlayWithAnotherPerson}
                                >
                                    <i className="bi bi-person-plus-fill" /> {t.result.anotherPlayerBtn}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ================================================================= */}
            {/* MODALES LEGALES (TÉRMINOS, PRIVACIDAD CCPA Y DO NOT SELL)        */}
            {/* ================================================================= */}
            {activeModal && (
                <div
                    className="PersonalRouletteFan__ModalOverlay"
                    onClick={() => setActiveModal(null)}
                >
                    <div
                        className="PersonalRouletteFan__ModalDialog"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="PersonalRouletteFan__ModalHeader">
                            <h5 className="PersonalRouletteFan__ModalTitle">
                                {activeModal === 'terms' && (
                                    <>
                                        <i className="bi bi-file-earmark-text-fill" /> {t.modals.termsTitle}
                                    </>
                                )}
                                {activeModal === 'privacy' && (
                                    <>
                                        <i className="bi bi-shield-check" /> {t.modals.privacyTitle}
                                    </>
                                )}
                                {activeModal === 'doNotSell' && (
                                    <>
                                        <i className="bi bi-person-x-fill" /> {t.modals.doNotSellTitle}
                                    </>
                                )}
                            </h5>
                            <button
                                type="button"
                                className="PersonalRouletteFan__ModalCloseBtn"
                                onClick={() => setActiveModal(null)}
                                aria-label="Cerrar modal"
                            >
                                <i className="bi bi-x" />
                            </button>
                        </div>

                        <div className="PersonalRouletteFan__ModalBody">
                            {activeModal === 'terms' && (
                                <>
                                    <div style={{
                                        background: 'rgba(245, 158, 11, 0.15)',
                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        color: '#fbbf24',
                                        fontWeight: '700',
                                        fontSize: '0.8rem',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <i className="bi bi-exclamation-triangle-fill" />
                                        {t.modals.ageConfirmationBadge}
                                    </div>
                                    {termsModalText}
                                </>
                            )}
                            {activeModal === 'privacy' && privacyModalText}
                            {activeModal === 'doNotSell' && doNotSellModalText}
                        </div>

                        <div className="PersonalRouletteFan__ModalFooter">
                            <button
                                type="button"
                                className="PersonalRouletteFan__ModalOkBtn"
                                onClick={() => setActiveModal(null)}
                            >
                                {t.modals.closeButton}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* NOTIFICACIÓN TOAST FLOTANTE */}
            {toastMessage && (
                <div className={`PersonalRouletteFan__Toast PersonalRouletteFan__Toast--${toastMessage.type}`}>
                    {toastMessage.type === 'success' && <i className="bi bi-check-circle-fill" />}
                    {toastMessage.type === 'danger' && <i className="bi bi-exclamation-triangle-fill" />}
                    {toastMessage.type === 'info' && <i className="bi bi-info-circle-fill" />}
                    <span>{toastMessage.msg}</span>
                </div>
            )}
        </div>
    );
};

export default PersonalRouletteFan;

